import { Link } from "react-router-dom";
import {
  ChevronLeft,
  Download,
  Mail,
  FileText,
  Image as ImageIcon,
  Mic,
} from "lucide-react";
import "./PressPage.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const KEY_FACTS = [
  { label: "Title", value: "Stones the Builder Refused, Volume I" },
  { label: "Subtitle", value: "50 African Americans Who Shaped America" },
  { label: "Author", value: "N.J. Griffin" },
  { label: "Publisher", value: "African American Xperience (self-published)" },
  { label: "Format", value: "8.5\" × 11\" full-color hardcover · 50 pages" },
  { label: "Price", value: "$1 digital · $24.99 hardcover" },
  { label: "Series", value: "Volume I of five (250 figures total)" },
  { label: "Available", value: "africanamericanxperience.com" },
];

const KEY_ANGLES = [
  {
    title: "The response to state-level Black-history restrictions",
    body: 'Griffin self-published Volume I in direct response to Black-history curriculum restrictions now in effect across 18 U.S. states. His tagline: "When they ban it in school, we put it in the home."',
  },
  {
    title: "The solo-builder story",
    body: "A Morris College graduate (class of 2001) from Mableton, Georgia, Griffin wrote, designed, coded the website, and set up print-on-demand fulfillment entirely on his own — no publisher, no team, no investor.",
  },
  {
    title: "The five-volume vision",
    body: "Volume I is the first of a planned five-volume series profiling 250 African American figures across civil rights, science, arts, athletics, invention, and letters.",
  },
  {
    title: "The $1 access tier",
    body: "A digital PDF is available for one dollar so cost is never the barrier to Black historical literacy, funded by hardcover sales at $24.99.",
  },
];

export default function PressPage() {
  return (
    <div className="press-root" data-testid="press-root">
      <div className="press-topbar">
        <Link to="/" className="press-back" data-testid="press-back-home">
          <ChevronLeft size={16} /> Back to site
        </Link>
        <div className="press-brand">PRESS KIT</div>
      </div>

      <header className="press-hero">
        <div className="press-hero-label">FOR IMMEDIATE RELEASE</div>
        <h1>Stones the Builder Refused, Volume I</h1>
        <p className="press-hero-sub">
          Press kit, downloadable assets, and interview requests for
          journalists, editors, podcasters, and radio producers.
        </p>
        <div className="press-hero-cta">
          <a
            className="press-btn-primary"
            href="mailto:press@africanamericanxperience.com?subject=Interview%20Request%20—%20Stones%20the%20Builder%20Refused"
            data-testid="press-contact"
          >
            <Mail size={16} /> Request an interview
          </a>
        </div>
      </header>

      <section className="press-section">
        <div className="press-section-label">01 · KEY FACTS</div>
        <div className="press-facts-grid" data-testid="press-facts">
          {KEY_FACTS.map((f) => (
            <div className="press-fact" key={f.label}>
              <div className="press-fact-label">{f.label}</div>
              <div className="press-fact-value">{f.value}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="press-section">
        <div className="press-section-label">02 · STORY ANGLES</div>
        <div className="press-angles">
          {KEY_ANGLES.map((a, i) => (
            <div className="press-angle" key={i}>
              <div className="press-angle-num">{String(i + 1).padStart(2, "0")}</div>
              <div>
                <h3>{a.title}</h3>
                <p>{a.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="press-section">
        <div className="press-section-label">03 · DOWNLOADS</div>
        <div className="press-downloads">
          <a
            className="press-download-card"
            href={`${API}/download/press-release`}
            target="_blank"
            rel="noreferrer"
            data-testid="press-dl-release"
          >
            <FileText size={22} />
            <div>
              <div className="dl-title">Press Release (PDF)</div>
              <div className="dl-sub">One-page official announcement</div>
            </div>
            <Download size={16} className="dl-icon" />
          </a>
          <a
            className="press-download-card"
            href={`${API}/download/founders-letter`}
            target="_blank"
            rel="noreferrer"
            data-testid="press-dl-founder"
          >
            <FileText size={22} />
            <div>
              <div className="dl-title">Founder&apos;s Letter (PDF)</div>
              <div className="dl-sub">N.J. Griffin&apos;s personal statement</div>
            </div>
            <Download size={16} className="dl-icon" />
          </a>
          <a
            className="press-download-card"
            href={`${API}/download/pdf`}
            target="_blank"
            rel="noreferrer"
            data-testid="press-dl-book"
          >
            <FileText size={22} />
            <div>
              <div className="dl-title">Full Book Preview (PDF)</div>
              <div className="dl-sub">Complete Volume I for editorial review</div>
            </div>
            <Download size={16} className="dl-icon" />
          </a>
          <a
            className="press-download-card"
            href="https://africanamericanxperience.com/"
            target="_blank"
            rel="noreferrer"
            data-testid="press-dl-cover"
          >
            <ImageIcon size={22} />
            <div>
              <div className="dl-title">Book Cover Image</div>
              <div className="dl-sub">Right-click / long-press to save from homepage</div>
            </div>
            <Download size={16} className="dl-icon" />
          </a>
        </div>
      </section>

      <section className="press-section">
        <div className="press-section-label">04 · AUTHOR BIO</div>
        <div className="press-bio">
          <p>
            <strong>N.J. Griffin</strong> is a first-time self-published author,
            Morris College graduate (Class of 2001, Criminal Justice and
            Psychology), and lifelong resident of the Atlanta metro area
            (Mableton, Georgia).
          </p>
          <p>
            Griffin conceived, wrote, designed, and self-published{" "}
            <em>Stones the Builder Refused, Volume I</em> in direct response to
            the Black-history curriculum restrictions currently in effect
            across 18 U.S. states. He built the associated website, integrated
            print-on-demand fulfillment through Lulu Press, and set up the
            entire sales and distribution infrastructure solo.
          </p>
          <p>
            Volume I is the first of a planned five-volume series profiling 250
            African American figures whose lives shaped the United States.
            Griffin is available for interviews, panels, and on-camera
            appearances.
          </p>
          <p className="press-bio-availability">
            <strong>Available for:</strong> radio interviews, podcast guest
            appearances, TV segments, panel discussions, HBCU colloquia,
            church programs, and community events.
          </p>
        </div>
      </section>

      <section className="press-section">
        <div className="press-section-label">05 · CONTACT</div>
        <div className="press-contact-block" data-testid="press-contact-block">
          <div>
            <div className="press-fact-label">Direct press email</div>
            <a
              href="mailto:press@africanamericanxperience.com?subject=Press%20Inquiry%20—%20Stones%20the%20Builder%20Refused"
              className="press-email"
            >
              press@africanamericanxperience.com
            </a>
          </div>
          <div className="press-response-note">
            <Mic size={14} /> Press inquiries answered within 24 hours.
          </div>
        </div>
      </section>

      <footer className="press-footer">
        <p>
          Stones the Builder Refused, Volume I &nbsp;·&nbsp;
          African American Xperience &nbsp;·&nbsp;
          <a href="https://africanamericanxperience.com/">africanamericanxperience.com</a>
        </p>
      </footer>
    </div>
  );
}
